import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import './ResultsPage.css';

/**
 * Renders the ResultsPage component, displaying validation results and offering download options
 * for validated data and mail merge templates.
 *
 * @param {Object} props - The properties object for the ResultsPage component.
 * @param {string} props.jobId - The unique identifier for the validation job.
 * @param {Object} props.validationData - The data object containing validation and assignment results.
 * @param {Object} props.validationData.sheetsResult - Results of the data validation process.
 * @param {Object} props.validationData.sheetsError - Error information related to the validation process.
 * @param {Object} props.validationData.thnksSelection - Selection data for gifts and assignments.
 * @param {Object[]} props.validationData.thnksSelection.gifts - Array of available gift options.
 * @param {Object} props.validationData.thnksSelection.assignments - Object mapping rows to gift assignments.
 *
 * @return {JSX.Element} The rendered ResultsPage component, including validation summaries, gift breakdowns,
 *     and download action buttons for processed data.
 */
function ResultsPage({ jobId, validationData }) {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingMailMerge, setIsDownloadingMailMerge] = useState(false);
  const [mailMergeError, setMailMergeError] = useState(null);

  // Get sheets result/error from validation data (auto-posted during validation)
  const sheetsResult = validationData?.sheetsResult;
  const sheetsError = validationData?.sheetsError;
  const thnksSelection = validationData?.thnksSelection;

  // Group assignments by gift to show breakdown
  const getGiftBreakdown = () => {
    if (!thnksSelection?.assignments || !thnksSelection?.gifts) return [];

    const breakdown = {};

    Object.entries(thnksSelection.assignments).forEach(([rowNumber, assignment]) => {
      const giftId = assignment.giftId;
      if (!breakdown[giftId]) {
        const gift = thnksSelection.gifts.find(g => g.id === giftId);
        breakdown[giftId] = {
          giftId,
          giftName: gift?.name || 'Unknown',
          costPerUnit: gift?.cost || 0,
          recipientCount: 0,
          totalCost: 0
        };
      }
      breakdown[giftId].recipientCount += 1;
      breakdown[giftId].totalCost += breakdown[giftId].costPerUnit;
    });

    return Object.values(breakdown).sort((a, b) => b.totalCost - a.totalCost);
  };

  const giftBreakdown = getGiftBreakdown();

  useEffect(() => {
    if (!jobId) {
      navigate('/');
      return;
    }

    fetchResults();
  }, [jobId]);

  const fetchResults = async () => {
    try {
      const response = await apiService.getValidationResults(jobId);
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  };

  const handleDownload = async (format = 'xlsx') => {
    setIsDownloading(true);
    try {
      const response = await apiService.downloadValidatedData(jobId, format);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `validated_addresses_${jobId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading:', error);
      alert('Failed to download file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleNewUpload = () => {
    navigate('/');
  };

  const handleDownloadMailMerge = async () => {
    setIsDownloadingMailMerge(true);
    setMailMergeError(null);
    try {
      const response = await apiService.downloadMailMerge(jobId);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mail_merge_${jobId.substring(0, 8)}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading mail merge:', error);
      setMailMergeError(
        error.response?.data?.detail || 'Failed to generate mail merge document. Please try again.'
      );
    } finally {
      setIsDownloadingMailMerge(false);
    }
  };

  if (!results) {
    return (
      <div className="results-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="results-page">
      <div className="success-header">
        <div className="success-icon">&#10003;</div>
        <h1>Order Complete!</h1>
        <p>Tracking information will be sent your way via email upon receipt of shipment</p>
      </div>

      {/* Thnks Selection Summary with Total Cost */}
      {thnksSelection && giftBreakdown.length > 0 && (
        <div className="thnks-summary">
          <div className="thnks-breakdown-list">
            {giftBreakdown.map(item => (
              <div key={item.giftId} className="thnks-info">
                <div className="thnks-detail">
                  <span className="label">Selected Thnks:</span>
                  <span className="value">{item.giftName}</span>
                </div>
                <div className="thnks-detail">
                  <span className="label">Recipients:</span>
                  <span className="value">{item.recipientCount}</span>
                </div>
                <div className="thnks-detail">
                  <span className="label">Cost per Thnks:</span>
                  <span className="value">${item.costPerUnit.toFixed(2)}</span>
                </div>
                <div className="thnks-detail subtotal">
                  <span className="label">Subtotal:</span>
                  <span className="value">${item.totalCost.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="total-cost-display">
            <span className="total-label">Total Cost</span>
            <span className="total-value">${thnksSelection.totalCost}</span>
          </div>
        </div>
      )}

      <div className="results-summary">
        <h2>Summary</h2>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Total Processed</span>
            <span className="summary-value">
              {results.valid_count + results.invalid_count + results.corrected_count + results.skipped_count}
            </span>
          </div>
          <div className="summary-item valid">
            <span className="summary-label">Valid</span>
            <span className="summary-value">{results.valid_count}</span>
          </div>
          <div className="summary-item corrected">
            <span className="summary-label">Corrected</span>
            <span className="summary-value">{results.corrected_count}</span>
          </div>
          <div className="summary-item invalid">
            <span className="summary-label">Invalid</span>
            <span className="summary-value">{results.invalid_count}</span>
          </div>
          <div className="summary-item skipped">
            <span className="summary-label">Skipped</span>
            <span className="summary-value">{results.skipped_count}</span>
          </div>
        </div>
      </div>

      <div className="action-section">
        <h3>Download Validated Data</h3>
        <p className="action-description">
          Download the validated addresses for your records or further processing.
        </p>

        <div className="download-options">
          <button
            onClick={() => handleDownload('xlsx')}
            className="btn-primary download-btn"
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download Excel'}
          </button>
          <button
            onClick={() => handleDownload('csv')}
            className="btn-secondary download-btn"
            disabled={isDownloading}
          >
            Download CSV
          </button>
        </div>

        <button
          onClick={handleNewUpload}
          className="btn-outline new-upload-btn"
        >
          Upload Another File
        </button>
        <p className="action-description-2">
          Questions: Please reach out to our team at <a href="mailto:support@thnks.com">support@thnks.com</a>
        </p>
      </div>
    </div>
  );
}

export default ResultsPage;